import { CheckCircle2, FileUp, Lock, Plus, Unlock } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { telechargerExportPresences, telechargerModelePointages, useActionsPresences, useImportsPointages, usePeriodesPresence, useRegistreAgent, useSynthesePresences } from '@/api/hooks';
import { CATEGORIES_SAISISSABLES, LIBELLE_CATEGORIE_PRESENCE, LIBELLE_SOURCE_PRESENCE, LIBELLE_TYPE_HEURES_SUP, type CategoriePresence, type ImportPointagesRapport, type TypeHeuresSup } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { CategoriePresenceBadge, StatutPeriodeBadge } from '@/components/presences';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr, montant, nombre } from '@/lib/utils';

const message = (e: unknown) => (e instanceof Error ? e.message : 'Erreur inattendue.');
const ONGLETS = ['agents', 'validation', 'pointages'] as const;
type Onglet = (typeof ONGLETS)[number];
const LIBELLE_ONGLET: Record<Onglet, string> = { agents: 'Agents', validation: 'Validation par structure', pointages: 'Pointages et clôture' };
const JOURS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
const jourSemaine = (iso: string) => JOURS[new Date(`${iso}T00:00:00Z`).getUTCDay()];

/**
 * Registre des présences (spécifications HRMS et Paie, § 6 ; lot HP-3) : synthèse par agent et par structure, fiche de
 * présence et heures supplémentaires d'un agent, validation par les responsables, importation des pointages, clôture.
 */
export function Presences() {
  const [params, setParams] = useSearchParams();
  const u = useSession()!.utilisateur;
  const gestion = u.profils.some((p) => ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'PAIE'].includes(p));
  const periodes = usePeriodesPresence();
  const mois = params.get('mois');
  const periode = periodes.data?.find((p) => p.mois === mois) ?? periodes.data?.find((p) => p.mois === new Date().toISOString().slice(0, 7)) ?? periodes.data?.[0];
  const onglet: Onglet = ONGLETS.includes(params.get('onglet') as Onglet) ? (params.get('onglet') as Onglet) : 'agents';
  const changer = (cle: string, valeur: string) => {
    const n = new URLSearchParams(params);
    if (valeur) n.set(cle, valeur);
    else n.delete(cle);
    setParams(n, { replace: true });
  };
  const actions = useActionsPresences();
  const [ouverture, setOuverture] = useState(false);
  if (periodes.isLoading) return <Chargement />;
  if (periodes.error) return <ErreurBloc erreur={periodes.error} />;
  return (
    <div className="space-y-6">
      <PageHeader
        titre={
          <span className="flex flex-wrap items-center gap-2">
            Présences et variables {periode && <>· {periode.mois} <StatutPeriodeBadge statut={periode.statut} /></>}
          </span>
        }
        sousTitre={periode ? `Du ${dateFr(periode.debut)} au ${dateFr(periode.fin)} (20 → 19, D17) · variables attendues avant le ${dateFr(periode.dateLimiteVariables)} · ${periode.agents} agent(s) · ${periode.structuresValidees}/${periode.structures} structure(s) validée(s) · ${periode.anomalies} anomalie(s)` : 'Aucune période ouverte.'}
        actions={
          <span className="flex flex-wrap items-center gap-2">
            {periodes.data && periodes.data.length > 0 && (
              <SelectNatif value={periode?.mois ?? ''} onChange={(e) => changer('mois', e.target.value)}>
                {periodes.data.map((p) => (
                  <option key={p.id} value={p.mois}>
                    {p.mois} · {p.statut === 'OUVERTE' ? 'ouverte' : 'clôturée'}
                  </option>
                ))}
              </SelectNatif>
            )}
            {gestion && (
              <Button variant="outline" onClick={() => setOuverture(true)}>
                <Plus className="size-4" /> Ouvrir une période
              </Button>
            )}
            {periode && (
              <Button variant="outline" onClick={() => telechargerExportPresences(periode.id, periode.mois, params.get('structure') ?? undefined).catch((e) => toast.error(message(e)))}>
                Exporter (Excel)
              </Button>
            )}
          </span>
        }
      />
      {periode && (
        <>
          <Tabs value={onglet} onValueChange={(v) => changer('onglet', v)}>
            <TabsList className="flex-wrap">
              {ONGLETS.filter((o) => o !== 'pointages' || gestion).map((o) => (
                <TabsTrigger key={o} value={o}>
                  {LIBELLE_ONGLET[o]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {onglet === 'agents' && <Agents periodeId={periode.id} structure={params.get('structure') ?? ''} q={params.get('q') ?? ''} anomalies={params.get('anomalies') ?? ''} changer={changer} agentId={params.get('agent')} />}
          {onglet === 'validation' && <Validation periodeId={periode.id} />}
          {onglet === 'pointages' && gestion && <Pointages periodeId={periode.id} statut={periode.statut} />}
        </>
      )}
      {ouverture && (
        <OuvertureDialog
          onClose={() => setOuverture(false)}
          onOuvrir={async (m, datePaiement) => {
            try {
              const p = await actions.ouvrirPeriode.mutateAsync({ mois: m, datePaiement: datePaiement || null });
              toast.success(`Période ${p.mois} ouverte : ${p.generation.agents} agent(s), ${p.generation.lignes} ligne(s) générée(s).`);
              changer('mois', p.mois);
              setOuverture(false);
            } catch (e) {
              toast.error(message(e));
            }
          }}
        />
      )}
    </div>
  );
}

function OuvertureDialog({ onClose, onOuvrir }: { onClose: () => void; onOuvrir: (mois: string, datePaiement: string) => Promise<void> }) {
  const [mois, setMois] = useState(new Date().toISOString().slice(0, 7));
  const [datePaiement, setDatePaiement] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-periode">
        <DialogHeader>
          <DialogTitle>Ouvrir une période de présence</DialogTitle>
          <DialogDescription>Mois de rémunération ; la période court du 20 du mois précédent au 19 du mois payé (D17) et le registre de tous les agents est généré depuis les congés, missions, fériés et repos.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label htmlFor="pe-mois">Mois (AAAA-MM) *</Label><Input id="pe-mois" value={mois} onChange={(e) => setMois(e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="pe-paiement">Date de paiement</Label><Input id="pe-paiement" type="date" value={datePaiement} onChange={(e) => setDatePaiement(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={() => onOuvrir(mois, datePaiement)} disabled={!/^\d{4}-\d{2}$/.test(mois)}>Ouvrir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Agents({ periodeId, structure, q, anomalies, changer, agentId }: { periodeId: string; structure: string; q: string; anomalies: string; changer: (c: string, v: string) => void; agentId: string | null }) {
  const { data, isLoading, error } = useSynthesePresences(periodeId, { structure: structure || undefined, q: q || undefined, anomalies: anomalies || undefined });
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const total = data.agents.length;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Agents" valeur={total} detail={`${data.agents.reduce((s, a) => s + a.aConfirmer, 0)} jour(s) présumé(s) sans pointage ni fiche`} />
        <Kpi label="Jours déduits" valeur={nombre(data.agents.reduce((s, a) => s + a.joursDeduits, 0), 1)} detail="absences non rémunérées (trentième, D19)" accent={data.agents.some((a) => a.joursDeduits > 0) ? 'alerte' : 'ok'} />
        <Kpi label="Anomalies" valeur={data.agents.reduce((s, a) => s + a.anomalies, 0)} detail="pointages à régler par les responsables" accent={data.agents.some((a) => a.anomalies > 0) ? 'alerte' : 'ok'} />
        <Kpi label="Heures sup. à valider" valeur={nombre(data.agents.reduce((s, a) => s + a.heuresSup.declarees, 0), 2)} detail={`${nombre(data.agents.reduce((s, a) => s + a.heuresSup.validees, 0), 2)} h validées`} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input className="w-64" placeholder="Nom, matricule…" value={q} onChange={(e) => changer('q', e.target.value)} />
        <SelectNatif value={structure} onChange={(e) => changer('structure', e.target.value)}>
          <option value="">Toutes les structures du périmètre</option>
          {data.structures.map((s) => (
            <option key={s.structure.code} value={s.structure.code}>
              {s.structure.code} · {s.structure.libelle}
            </option>
          ))}
        </SelectNatif>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={anomalies === 'true'} onChange={(e) => changer('anomalies', e.target.checked ? 'true' : '')} /> Anomalies ou déclarations en attente
        </label>
      </div>
      {agentId && <RegistreAgent periodeId={periodeId} agentId={agentId} onClose={() => changer('agent', '')} />}
      {!total ? (
        <Vide message="Aucun agent ne correspond." />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table data-testid="synthese-presences">
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="text-right">Prestés</TableHead>
                <TableHead className="hidden md:table-cell text-right">Congés · incapacité</TableHead>
                <TableHead className="text-right">Déduits</TableHead>
                <TableHead className="hidden lg:table-cell text-right">Heures sup.</TableHead>
                <TableHead className="hidden lg:table-cell">Anomalies</TableHead>
                <TableHead>Validation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.agents.map((a) => (
                <TableRow key={a.agent.id} className={agentId === a.agent.id ? 'bg-muted/50' : ''}>
                  <TableCell>
                    <button type="button" className="font-medium text-primary hover:underline" onClick={() => changer('agent', a.agent.id)}>
                      {a.agent.prenom} {a.agent.nom}
                    </button>
                    <div className="text-xs text-muted-foreground">
                      {a.agent.matricule} · {a.agent.structure?.code ?? '—'}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {nombre(a.jours.PRESTE + a.jours.MISSION, 1)}
                    {a.aConfirmer > 0 && <div className="text-xs text-muted-foreground">{a.aConfirmer} présumé(s)</div>}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-right">{nombre(a.jours.CONGE_ANNUEL + a.jours.CONGE_CIRCONSTANCE + a.jours.CONGE_ACCOUCHEMENT + a.jours.INCAPACITE, 1)}</TableCell>
                  <TableCell className={`text-right ${a.joursDeduits > 0 ? 'text-destructive' : ''}`}>
                    {nombre(a.joursDeduits, 1)}
                    {a.joursSansAllocation > 0 && <div className="text-xs">{nombre(a.joursSansAllocation, 1)} sans allocation</div>}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-right">
                    {nombre(a.heuresSup.validees, 2)} h{a.heuresSup.declarees > 0 ? <div className="text-xs text-amber-700">{nombre(a.heuresSup.declarees, 2)} h à valider</div> : null}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">{a.anomalies ? <span className="text-amber-700">{a.anomalies}</span> : <span className="text-emerald-700">aucune</span>}</TableCell>
                  <TableCell>{a.validation === 'VALIDEE' ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">validée</Badge> : <Badge variant="secondary">en cours</Badge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function RegistreAgent({ periodeId, agentId, onClose }: { periodeId: string; agentId: string; onClose: () => void }) {
  const { data, isLoading, error } = useRegistreAgent(periodeId, agentId);
  const actions = useActionsPresences();
  const [fiche, setFiche] = useState<{ date: string; categorie: CategoriePresence } | null>(null);
  const [hs, setHs] = useState(false);
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const decider = async (id: string, decision: 'VALIDER' | 'REFUSER') => {
    const motif = decision === 'REFUSER' ? window.prompt('Motif du refus') ?? '' : undefined;
    if (decision === 'REFUSER' && !motif) return;
    try {
      await actions.deciderHeuresSup.mutateAsync({ periodeId, id, corps: { decision, motif } });
      toast.success(decision === 'VALIDER' ? 'Heures validées.' : 'Déclaration refusée.');
    } catch (e) {
      toast.error(message(e));
    }
  };
  const s = data.synthese;
  return (
    <Card data-testid="registre-agent">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span>
            {data.agent.prenom} {data.agent.nom} · {data.agent.matricule} · {data.agent.structure?.code ?? '—'}
          </span>
          <span className="flex flex-wrap gap-2">
            {data.droits.declarerHeuresSup && (
              <Button size="sm" variant="outline" onClick={() => setHs(true)}>
                <Plus className="size-4" /> Heures supplémentaires
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onClose}>
              Fermer
            </Button>
          </span>
        </CardTitle>
        <CardDescription>
          {nombre(s.jours.PRESTE + s.jours.MISSION, 1)} jour(s) presté(s) · {nombre(s.joursDeduits, 1)} déduit(s) · {nombre(s.heuresSup.validees, 2)} h sup. validées
          {s.montantsHeuresSup && s.montantsHeuresSup.total !== null ? ` (${montant(s.montantsHeuresSup.total, true)} au taux horaire de ${montant(s.montantsHeuresSup.tauxHoraire, true)})` : ''}
          {!data.eligibilite.heuresSup ? ' · non éligible aux heures supplémentaires' : ''}
          {data.droits.saisir ? ' · cliquez un jour pour le corriger (fiche de présence).' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-7 gap-1 text-xs" data-testid="calendrier-agent">
          {data.lignes.map((l) => (
            <button
              key={l.date}
              type="button"
              disabled={!data.droits.saisir || l.categorie === 'REPOS' || l.categorie === 'FERIE'}
              onClick={() => setFiche({ date: l.date, categorie: l.categorie })}
              title={`${LIBELLE_CATEGORIE_PRESENCE[l.categorie]} · ${LIBELLE_SOURCE_PRESENCE[l.source]}${l.anomalie ? ` · ${l.anomalie}` : ''}`}
              className={`rounded border p-1 text-left ${l.anomalie ? 'border-amber-400' : ''} ${data.droits.saisir && l.categorie !== 'REPOS' && l.categorie !== 'FERIE' ? 'hover:bg-muted' : 'opacity-80'}`}
            >
              <div className="text-muted-foreground">
                {jourSemaine(l.date)} {l.date.slice(8)}
              </div>
              <CategoriePresenceBadge categorie={l.categorie} fraction={l.fraction} />
              {l.heuresPrestees !== null && <div className="text-muted-foreground">{nombre(l.heuresPrestees, 1)} h</div>}
            </button>
          ))}
        </div>
        {data.heuresSup.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Heures</TableHead>
                <TableHead>Statut</TableHead>
                {data.droits.validerHeuresSup && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.heuresSup.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>{dateFr(h.date)} · {h.semaine}</TableCell>
                  <TableCell>
                    {h.libelleType}
                    {h.motif ? <div className="text-xs text-muted-foreground">{h.motif} · déclarées par {h.declarePar ?? '—'}</div> : null}
                  </TableCell>
                  <TableCell>{nombre(h.heures, 2)} h</TableCell>
                  <TableCell>
                    <Badge variant={h.statut === 'VALIDEE' ? 'default' : h.statut === 'REFUSEE' ? 'destructive' : 'secondary'}>{h.statut === 'VALIDEE' ? 'Validée' : h.statut === 'REFUSEE' ? 'Refusée' : 'Déclarée'}</Badge>
                    {h.validePar ? <div className="text-xs text-muted-foreground">par {h.validePar} le {dateHeureFr(h.valideLe)}</div> : null}
                  </TableCell>
                  {data.droits.validerHeuresSup && (
                    <TableCell className="text-right">
                      {h.statut === 'DECLAREE' && (
                        <span className="inline-flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => decider(h.id, 'VALIDER')}>
                            <CheckCircle2 className="size-4" /> Valider
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => decider(h.id, 'REFUSER')}>
                            Refuser
                          </Button>
                        </span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {fiche && <FicheDialog periodeId={periodeId} agentId={agentId} date={fiche.date} categorie={fiche.categorie} onClose={() => setFiche(null)} />}
      {hs && <HeuresSupDialog periodeId={periodeId} agentId={agentId} astreinte={data.eligibilite.astreinte} onClose={() => setHs(false)} />}
    </Card>
  );
}

function FicheDialog({ periodeId, agentId, date, categorie, onClose }: { periodeId: string; agentId: string; date: string; categorie: CategoriePresence; onClose: () => void }) {
  const actions = useActionsPresences();
  const [s, setS] = useState({ categorie: (CATEGORIES_SAISISSABLES.includes(categorie) ? categorie : 'PRESTE') as CategoriePresence, fraction: '1', heureArrivee: '', heureDepart: '', commentaire: '' });
  const valider = async () => {
    try {
      await actions.fiche.mutateAsync({ periodeId, agentId, corps: { lignes: [{ date, categorie: s.categorie, fraction: Number(s.fraction) as 1 | 0.5, heureArrivee: s.heureArrivee || null, heureDepart: s.heureDepart || null, commentaire: s.commentaire || null }] } });
      toast.success('Fiche de présence enregistrée.');
      onClose();
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-fiche">
        <DialogHeader>
          <DialogTitle>Présence du {dateFr(date)}</DialogTitle>
          <DialogDescription>Fiche de présence du responsable (zones sans pointage) ou correction ; l'incapacité et la mise à pied exigent un justificatif ou un commentaire.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label htmlFor="fi-cat">Catégorie *</Label><SelectNatif id="fi-cat" value={s.categorie} onChange={(e) => setS({ ...s, categorie: e.target.value as CategoriePresence })}>{CATEGORIES_SAISISSABLES.map((c) => <option key={c} value={c}>{LIBELLE_CATEGORIE_PRESENCE[c]}</option>)}</SelectNatif></div>
          <div className="space-y-1"><Label htmlFor="fi-fraction">Fraction</Label><SelectNatif id="fi-fraction" value={s.fraction} onChange={(e) => setS({ ...s, fraction: e.target.value })}><option value="1">Journée</option><option value="0.5">Demi-journée</option></SelectNatif></div>
          <div className="space-y-1"><Label htmlFor="fi-arrivee">Arrivée</Label><Input id="fi-arrivee" type="time" value={s.heureArrivee} onChange={(e) => setS({ ...s, heureArrivee: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="fi-depart">Départ</Label><Input id="fi-depart" type="time" value={s.heureDepart} onChange={(e) => setS({ ...s, heureDepart: e.target.value })} /></div>
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="fi-com">Commentaire</Label><Textarea id="fi-com" value={s.commentaire} onChange={(e) => setS({ ...s, commentaire: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={actions.fiche.isPending}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HeuresSupDialog({ periodeId, agentId, astreinte, onClose }: { periodeId: string; agentId: string; astreinte: boolean; onClose: () => void }) {
  const actions = useActionsPresences();
  const [s, setS] = useState({ date: '', type: 'SEMAINE' as TypeHeuresSup, heures: '', motif: '' });
  const valider = async () => {
    try {
      await actions.declarerHeuresSup.mutateAsync({ periodeId, agentId, corps: { date: s.date, type: s.type, heures: Number(s.heures), motif: s.motif || null } });
      toast.success('Heures déclarées : validation par un autre responsable ou les RH.');
      onClose();
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-heures-sup">
        <DialogHeader>
          <DialogTitle>Déclarer des heures supplémentaires</DialogTitle>
          <DialogDescription>Semaine : six premières heures à 40 %, puis 70 % (décompte hebdomadaire continu) ; repos et fériés travaillés au taux spécifique ; astreinte décomptée à part (D20).</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label htmlFor="hs-date">Date *</Label><Input id="hs-date" type="date" value={s.date} onChange={(e) => setS({ ...s, date: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="hs-type">Type *</Label><SelectNatif id="hs-type" value={s.type} onChange={(e) => setS({ ...s, type: e.target.value as TypeHeuresSup })}>{(Object.keys(LIBELLE_TYPE_HEURES_SUP) as TypeHeuresSup[]).filter((t) => t !== 'ASTREINTE' || astreinte).map((t) => <option key={t} value={t}>{LIBELLE_TYPE_HEURES_SUP[t]}</option>)}</SelectNatif></div>
          <div className="space-y-1"><Label htmlFor="hs-heures">Heures *</Label><Input id="hs-heures" type="number" step="0.25" min="0.25" max="24" value={s.heures} onChange={(e) => setS({ ...s, heures: e.target.value })} /></div>
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="hs-motif">Motif</Label><Input id="hs-motif" value={s.motif} onChange={(e) => setS({ ...s, motif: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={!s.date || !s.heures || actions.declarerHeuresSup.isPending}>Déclarer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Validation({ periodeId }: { periodeId: string }) {
  const { data, isLoading, error } = useSynthesePresences(periodeId, {});
  const actions = useActionsPresences();
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const valider = async (code: string) => {
    try {
      await actions.validerStructure.mutateAsync({ periodeId, corps: { structureCode: code } });
      toast.success(`Registre de ${code} validé.`);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Card data-testid="validation-structures">
      <CardHeader>
        <CardTitle>Validation par structure</CardTitle>
        <CardDescription>Chaque directeur (ou responsable local d'une coordination) valide le registre de sa structure une fois les anomalies réglées et les heures supplémentaires décidées ; toute modification ultérieure rouvre la validation.</CardDescription>
      </CardHeader>
      <CardContent>
        {!data.structures.length ? (
          <Vide message="Aucune structure dans votre périmètre." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Structure</TableHead>
                <TableHead className="text-right">Agents</TableHead>
                <TableHead className="text-right">Anomalies</TableHead>
                <TableHead className="text-right">Heures sup. à valider</TableHead>
                <TableHead className="text-right">Jours déduits</TableHead>
                <TableHead>Validation</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.structures.map((s) => (
                <TableRow key={s.structure.id}>
                  <TableCell>
                    <Link to={`/paie/presences?mois=${data.periode.mois}&structure=${s.structure.code}`} className="font-medium text-primary hover:underline">
                      {s.structure.code}
                    </Link>
                    <div className="text-xs text-muted-foreground">{s.structure.libelle}</div>
                  </TableCell>
                  <TableCell className="text-right">{s.agents}</TableCell>
                  <TableCell className={`text-right ${s.anomalies ? 'text-amber-700' : ''}`}>{s.anomalies}</TableCell>
                  <TableCell className={`text-right ${s.heuresSupDeclarees ? 'text-amber-700' : ''}`}>{nombre(s.heuresSupDeclarees, 2)}</TableCell>
                  <TableCell className="text-right">{nombre(s.joursDeduits, 1)}</TableCell>
                  <TableCell>
                    {s.validation === 'VALIDEE' ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">validée le {dateFr(s.valideLe)}</Badge> : <Badge variant="secondary">en cours</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    {s.peutValider && s.validation !== 'VALIDEE' && (
                      <Button size="sm" onClick={() => valider(s.structure.code)} disabled={actions.validerStructure.isPending} data-testid={`valider-${s.structure.code}`}>
                        <CheckCircle2 className="size-4" /> Valider
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Pointages({ periodeId, statut }: { periodeId: string; statut: 'OUVERTE' | 'CLOTUREE' }) {
  const actions = useActionsPresences();
  const imports = useImportsPointages(periodeId);
  const u = useSession()!.utilisateur;
  const drh = u.profils.includes('DIRECTEUR_RH');
  const [fichier, setFichier] = useState<File | null>(null);
  const [rapport, setRapport] = useState<ImportPointagesRapport | null>(null);
  const [motif, setMotif] = useState('');
  const importer = async (simulation: boolean) => {
    if (!fichier) return;
    try {
      const r = await actions.importerPointages.mutateAsync({ periodeId, fichier, simulation });
      setRapport(r);
      if (!simulation) toast.success(`${r.integrees} pointage(s) intégré(s), ${r.rejetees} rejeté(s).`);
    } catch (e) {
      toast.error(message(e));
    }
  };
  const cloturer = async () => {
    try {
      await actions.cloturer.mutateAsync({ periodeId, motif: motif || undefined });
      toast.success('Période clôturée : présences définitives pour la paie.');
    } catch (e) {
      toast.error(message(e));
    }
  };
  const rouvrir = async () => {
    try {
      await actions.rouvrir.mutateAsync({ periodeId, motif });
      toast.success('Période rouverte.');
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Importer les pointages</CardTitle>
          <CardDescription>Fichier du système de pointage (Matricule, Date, Entrée, Sortie ou Heures) pour les zones couvertes ; contrôlez d'abord, puis écrivez. Les doublons et matricules inconnus sont rejetés, les pointages un jour de congé, de mission ou férié sont signalés, un pointage court vaut une demi-journée.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="pt-fichier">Classeur (.xlsx)</Label>
              <Input id="pt-fichier" type="file" accept=".xlsx" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} data-testid="champ-pointages" />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => telechargerModelePointages().catch((e) => toast.error(message(e)))}>
              Modèle
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={!fichier || statut !== 'OUVERTE' || actions.importerPointages.isPending} onClick={() => importer(true)}>
              Contrôler
            </Button>
            <Button type="button" size="sm" disabled={!fichier || statut !== 'OUVERTE' || actions.importerPointages.isPending} onClick={() => importer(false)}>
              <FileUp className="size-4" /> Écrire
            </Button>
          </div>
          {rapport && (
            <div className="text-sm">
              <div className="font-medium">
                {rapport.simulation ? 'Contrôle' : 'Écriture'} : {rapport.lignes} ligne(s), {rapport.integrees} intégrée(s), {rapport.rejetees} rejetée(s)
              </div>
              {rapport.anomalies.length > 0 && (
                <ul className="list-inside list-disc text-amber-800">
                  {rapport.anomalies.map((a, i) => (
                    <li key={i} className={a.bloquante ? 'text-destructive' : ''}>
                      Ligne {a.ligne} · {a.matricule ?? '—'} · {a.date ?? '—'} : {a.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {imports.data && imports.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Fichier</TableHead>
                  <TableHead>Lignes</TableHead>
                  <TableHead>Intégrées / rejetées</TableHead>
                  <TableHead>Anomalies</TableHead>
                  <TableHead className="hidden md:table-cell">Par</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {imports.data.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{dateHeureFr(i.createdAt)}</TableCell>
                    <TableCell>{i.fichier}</TableCell>
                    <TableCell>{i.lignes}</TableCell>
                    <TableCell>
                      {i.integrees} / {i.rejetees}
                    </TableCell>
                    <TableCell>{i.anomalies}</TableCell>
                    <TableCell className="hidden md:table-cell">{i.auteur ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{statut === 'OUVERTE' ? 'Clôturer la période' : 'Période clôturée'}</CardTitle>
          <CardDescription>{statut === 'OUVERTE' ? 'Toutes les structures validées et aucune anomalie ; le Directeur RH peut clôturer avec un motif. Les présences deviennent définitives pour la paie.' : 'Les présences sont figées ; le Directeur RH peut rouvrir la période avec un motif (variables tardives).'}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="cl-motif">{statut === 'OUVERTE' ? 'Motif (Directeur RH, si des structures ne sont pas validées)' : 'Motif de réouverture *'}</Label>
            <Input id="cl-motif" className="w-96" value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
          {statut === 'OUVERTE' ? (
            <Button onClick={cloturer} disabled={actions.cloturer.isPending} data-testid="cloturer-periode">
              <Lock className="size-4" /> Clôturer
            </Button>
          ) : (
            drh && (
              <Button variant="outline" onClick={rouvrir} disabled={!motif.trim() || actions.rouvrir.isPending}>
                <Unlock className="size-4" /> Rouvrir
              </Button>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
